import type { FC } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import type { FormFields } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Checkbox, Divider, Flex, FormItem, Input, Progress, RadioGroup, Typography } from '@ui';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePageContext } from 'vike-react/usePageContext';
import { reload } from 'vike/client/router';
import { applicationsPost } from '@/api/apply/applications/applicationsPost';
import { calculateProgress } from '@/pages/apply/components/ApplyForm/calculateProgress';
import { getLinkHref } from '@/utils/getLinkHref';
import styles from './ApplyForm.module.css';
import { applySchema } from './schema';

const radioItems = [
    { value: 'true', label: 'Affirm' },
    { value: 'false', label: 'Negative' }
];

export const ApplyForm: FC = () => {
    const pageContext = usePageContext();
    const { t } = useTranslation();

    const { control, reset, handleSubmit, formState: { isSubmitting, errors } } = useForm<FormFields>({
        defaultValues: {
            city: '',
            timezone: ''
        },
        resolver: zodResolver(applySchema(t)),
        mode: 'all'
    });
    const values = useWatch({ control });

    const disabled = !pageContext.user || pageContext.user.role !== 'pilot';

    const onSubmit: SubmitHandler<FormFields> = (data) => {
        if (disabled)
            return;

        if (data.age < 13) {
            return location.href = 'https://vats.im/cor#article-i-membership';
        }

        applicationsPost(data)
            .then(() => {
                reset();
                void reload();
            })
            .catch((e) => {
                console.error(e);
                throw e;
            });
    };

    return (
        // eslint-disable-next-line ts/no-misused-promises
        <form onSubmit={handleSubmit(onSubmit)} className={styles.applyForm}>
            <Progress
                title={t('Apply_Form_Already_Completed')}
                value={calculateProgress(values, errors, t)}
            />
            <FormItem
                label={t('Apply_Form_City_Label')}
                control={control}
                name="city"
                render={({ field }) => (
                    <Input
                        disabled={disabled}
                        placeholder={t('Apply_Form_City_Placeholder')}
                        {...field}
                    />
                )}
            />
            <Flex gap={16}>
                <FormItem
                    label={t('Apply_Form_Timezone_Label')}
                    control={control}
                    name="timezone"
                    render={({ field }) => (
                        <Input
                            disabled={disabled}
                            placeholder={t('Apply_Form_Timezone_Placeholder')}
                            {...field}
                        />
                    )}
                />
                <FormItem
                    label={t('Apply_Form_Age_Label')}
                    control={control}
                    name="age"
                    render={({ field: { value, onChange, ...props } }) => (
                        <Input
                            disabled={disabled}
                            placeholder={t('Apply_Form_Age_Placeholder')}
                            type="number"
                            min={1}
                            value={value || ''}
                            onChange={e => onChange(+e.target.value)}
                            {...props}
                            block
                        />
                    )}
                />
            </Flex>
            <FormItem
                label={t('Apply_Form_Irl_Experience')}
                control={control}
                name="irl_experience"
                render={({ field: { value, onChange, ...props } }) => (
                    <RadioGroup
                        disabled={disabled}
                        items={radioItems}
                        value={typeof value === 'boolean' ? String(value) : ''}
                        onChange={v => onChange(v === 'true')}
                        {...props}
                    />
                )}
            />
            <FormItem
                control={control}
                name="vatrus_apply"
                render={({ field: { value, onChange, ...props } }) => (
                    <Checkbox
                        disabled={disabled}
                        label={(
                            <>
                                {t('Apply_Form_Vatrus_Apply_Text')}
                                {' '}
                                <a
                                    href="https://vatrus.info/training"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className={styles.link}
                                >
                                    {t('Apply_Form_Vatrus_Apply_Link')}
                                </a>
                            </>
                        )}
                        checked={value || false}
                        onCheckedChange={onChange}
                        {...props}
                    />
                )}
            />
            <div className={styles.divider}>
                <Divider width="100%" />
            </div>
            <Flex justify="space-between" align="center" gap={36}>
                <Button type="submit" loading={isSubmitting} disabled={disabled}>{t('Apply_Form_Submit_Button')}</Button>
                <Flex flex="1 0">
                    <Typography.Paragraph level={3} color="textMain">
                        {t('Apply_Form_Privacy_Policy_Text')}
                        {' '}
                        <a href={getLinkHref(pageContext, '/privacy')} className={styles.link}>
                            {t('Apply_Form_Privacy_Policy_Link')}
                        </a>
                    </Typography.Paragraph>
                </Flex>
            </Flex>
        </form>
    );
};
